/**
 * InventoryContractIntegration - Reserve/Assign Inventory to Contracts
 * 
 * Features:
 * - View BOM items for a contract
 * - Search and select available inventory items by catalog item
 * - Reserve items for contract
 * - Assign items to contract
 * - View already assigned inventory
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Package,
  Search,
  CheckCircle2,
  Clock,
  ClipboardList,
  Loader2,
  Plus,
  Link2,
  Unlink,
  AlertTriangle,
  Box,
  Warehouse,
  ChevronRight
} from 'lucide-react';

// Status configuration matching InventoryPage
const STATUS_CONFIG = {
  disponibile: { label: 'Disponibile', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  riservato: { label: 'Riservato', color: 'bg-amber-100 text-amber-700', icon: Clock },
  assegnato: { label: 'Assegnato', color: 'bg-blue-100 text-blue-700', icon: ClipboardList },
};

export default function InventoryContractIntegration({ 
  contractId, 
  contractNumber,
  nodeId,
  readOnly = false 
}) {
  const [loading, setLoading] = useState(true);
  const [bomItems, setBomItems] = useState([]);
  const [contractInventory, setContractInventory] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  
  // Dialog states
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedBomItem, setSelectedBomItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  const filteredAvailableItems = useMemo(
    () => availableItems.filter(item =>
      !searchTerm ||
      item.serial_number.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [availableItems, searchTerm]
  );

  // Load BOM for contract
  const loadBom = useCallback(async () => {
    if (!contractId || !nodeId) return;
    
    try {
      const response = await api.get(`/catalog/bom/${contractId}/${nodeId}`);
      setBomItems(response.data.items || []);
    } catch (error) {
      console.error('Error loading BOM:', error);
    }
  }, [contractId, nodeId]);

  // Load inventory assigned to contract
  const loadContractInventory = useCallback(async () => {
    if (!contractId) return;
    
    try {
      const response = await api.get(`/inventory/by-contract/${contractId}`);
      setContractInventory(response.data.items || []);
    } catch (error) {
      console.error('Error loading contract inventory:', error);
    }
  }, [contractId]);

  // Load available inventory for a catalog item
  const loadAvailableItems = useCallback(async (catalogItemId) => {
    try {
      const response = await api.get(`/inventory/items?stato=disponibile&limit=100`);
      // Filter by catalog_item_id on client side
      const filtered = (response.data.items || []).filter(
        item => item.catalog_item_id === catalogItemId
      );
      setAvailableItems(filtered);
    } catch (error) {
      console.error('Error loading available items:', error);
      setAvailableItems([]);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadBom(), loadContractInventory()]);
      setLoading(false);
    };
    loadData();
  }, [loadBom, loadContractInventory]);

  // Open assign dialog for a BOM item
  const handleOpenAssignDialog = (bomItem) => {
    setSelectedBomItem(bomItem);
    setSearchTerm('');
    loadAvailableItems(bomItem.catalog_item_id);
    setShowAssignDialog(true);
  };

  // Reserve item for contract
  const handleReserveItem = async (inventoryItem) => {
    setSaving(true);
    try {
      await api.post(`/inventory/items/${inventoryItem.id}/reserve`, {
        contract_id: contractId,
        contract_number: contractNumber,
        note: `Riservato per BOM - ${selectedBomItem?.brand} ${selectedBomItem?.model}`
      });
      toast.success(`Articolo ${inventoryItem.serial_number} riservato`);
      loadContractInventory();
      loadAvailableItems(selectedBomItem.catalog_item_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella riserva');
    } finally {
      setSaving(false);
    }
  };

  // Assign item to contract
  const handleAssignItem = async (inventoryItem) => {
    setSaving(true);
    try {
      await api.post(`/inventory/items/${inventoryItem.id}/assign`, {
        contract_id: contractId,
        contract_number: contractNumber,
        note: `Assegnato per BOM - ${selectedBomItem?.brand} ${selectedBomItem?.model}`
      });
      toast.success(`Articolo ${inventoryItem.serial_number} assegnato`);
      loadContractInventory();
      loadAvailableItems(selectedBomItem.catalog_item_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'assegnazione');
    } finally {
      setSaving(false);
    }
  };

  // Cancel reservation
  const handleCancelReservation = async (inventoryItem) => {
    setSaving(true);
    try {
      await api.post(`/inventory/items/${inventoryItem.id}/cancel-reservation`);
      toast.success('Riserva annullata');
      loadContractInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'annullamento');
    } finally {
      setSaving(false);
    }
  };

  // Get count of assigned inventory for a catalog item
  const getAssignedCount = (catalogItemId) => {
    return contractInventory.filter(inv => inv.catalog_item_id === catalogItemId).length;
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.disponibile;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <Package className="h-5 w-5" />
            Materiali Assegnati
          </h3>
          <p className="text-sm text-muted-foreground">
            Gestisci l'inventario fisico per questo contratto
          </p>
        </div>
        <Badge variant="outline" className="text-lg px-3 py-1">
          {contractInventory.length} articoli collegati
        </Badge>
      </div>

      {/* BOM Items with Inventory Status */}
      {bomItems.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Distinta Base (BOM)</CardTitle>
            <CardDescription>
              Collega gli articoli fisici dall'inventario
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Articolo</TableHead>
                  <TableHead className="text-center">Qtà BOM</TableHead>
                  <TableHead className="text-center">Collegati</TableHead>
                  <TableHead className="text-center">Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bomItems.map((item, idx) => {
                  const assignedCount = getAssignedCount(item.catalog_item_id);
                  const isComplete = assignedCount >= item.quantity;
                  const itemKey = `bom-${item.catalog_item_id || item.item_code || idx}-${idx}`;
                  
                  return (
                    <TableRow key={itemKey}>
                      <TableCell>
                        <div className="font-medium">{item.brand}</div>
                        <div className="text-sm text-muted-foreground">{item.model}</div>
                        <div className="text-xs text-muted-foreground">{item.type_code}</div>
                      </TableCell>
                      <TableCell className="text-center font-mono">
                        {item.quantity}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge 
                          variant="outline"
                          className={isComplete ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}
                        >
                          {assignedCount} / {item.quantity}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {isComplete ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {!readOnly && !isComplete && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleOpenAssignDialog(item)}
                          >
                            <Link2 className="h-4 w-4 mr-1" />
                            Collega
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <Box className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Nessun articolo nella distinta base</p>
            <p className="text-sm text-muted-foreground mt-1">
              Aggiungi articoli dal Configuratore Tecnico prima di collegare l'inventario
            </p>
          </CardContent>
        </Card>
      )}

      {/* Assigned Inventory List */}
      {contractInventory.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Inventario Collegato</CardTitle>
            <CardDescription>
              Articoli fisici riservati/assegnati a questo contratto
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Seriale</TableHead>
                    <TableHead>Articolo</TableHead>
                    <TableHead>Magazzino</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contractInventory.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-sm">
                        {item.serial_number}
                        {item.lotto && (
                          <span className="block text-xs text-muted-foreground">
                            Lotto: {item.lotto}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{item.brand}</div>
                        <div className="text-xs text-muted-foreground">{item.model}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Warehouse className="h-3 w-3" />
                          {item.warehouse_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={item.stato} />
                      </TableCell>
                      <TableCell className="text-right">
                        {!readOnly && item.stato === 'riservato' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleCancelReservation(item)}
                            disabled={saving}
                          >
                            <Unlink className="h-4 w-4 mr-1" />
                            Annulla
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Collega Inventario</DialogTitle>
            <DialogDescription>
              Seleziona articoli disponibili da assegnare al contratto
            </DialogDescription>
          </DialogHeader>
          
          {selectedBomItem && (
            <div className="space-y-4">
              {/* Selected BOM item info */}
              <div className="bg-muted/50 p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedBomItem.brand} {selectedBomItem.model}</p>
                    <p className="text-sm text-muted-foreground">{selectedBomItem.type_code}</p>
                  </div>
                  <Badge>
                    {getAssignedCount(selectedBomItem.catalog_item_id)} / {selectedBomItem.quantity} collegati
                  </Badge>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per seriale..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Available items */}
              <ScrollArea className="h-64 border rounded-lg">
                {availableItems.length === 0 ? (
                  <div className="p-8 text-center">
                    <Package className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground">
                      Nessun articolo disponibile per questo prodotto
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Aggiungi articoli all'inventario dalla pagina Inventario
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Seriale</TableHead>
                        <TableHead>Magazzino</TableHead>
                        <TableHead>Ubicazione</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAvailableItems
                        .map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-mono text-sm">
                              {item.serial_number}
                            </TableCell>
                            <TableCell className="text-sm">
                              {item.warehouse_name}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {item.ubicazione || '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleReserveItem(item)}
                                  disabled={saving}
                                >
                                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Clock className="h-3 w-3 mr-1" />}
                                  Riserva
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleAssignItem(item)}
                                  disabled={saving}
                                >
                                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <ClipboardList className="h-3 w-3 mr-1" />}
                                  Assegna
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </ScrollArea>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
